<template>
  <main class="container-wide">
    <div v-if="errorResponse">
      <WarningPanel
        :title="'There\'s a problem...'"
        :msg="errorResponse"
        :severe="true"
      />
    </div>
    <div v-else>
      <div
        class="agent-message"
        v-if="agentMessage && agentMessage.length > 0"
        v-html="agentMessage"
      />

      <h1 class="page-title" data-testid="page-title">
        Your Utilities Package
      </h1>
      <p class="intro">
        Your housemate <strong>{{ accountCreator }}</strong> has created this
        account for you to join at
        <strong class="address">{{ address }}</strong>
      </p>

      <ServiceSummary
        class="summary page-wrapper"
        :joining-tenant="true"
        :ppppw="pricePerPersonPerWeek"
        :ppppm="pricePerPersonMonthly"
        :non-discounted-ppppw="nonDiscountedPricePerPersonPerWeek"
        :non-discounted-ppppm="nonDiscountedPricePerPersonPerMonthly"
        :tenancy-start-date="tenancyStartDate"
        :tenancy-end-date="tenancyEndDate"
        :first-payment-date="tenancyStartDate"
        :broadband-date="broadbandInstallationDate"
        :discount-description="discountDescription"
      />

      <button
        class="btn btn--big join-button"
        data-testid="submit-join-next"
        @click="formSubmit"
        id="joining-tenant-summary-conversion"
      >
        Next
      </button>
    </div>
  </main>
</template>

<script>
import { mapActions, mapGetters, mapMutations, mapState } from "vuex";
import WarningPanel from "@/components/WarningPanel";
import ServiceSummary from "@/components/ServiceSummary";

export default {
  name: "JoiningSummary",
  data() {
    return {
      errorResponse: "",
    };
  },
  computed: {
    ...mapState({
      agentMessage({ accountInformation }) {
        return accountInformation.agentMessage;
      },
      accountCreator({ accountInformation }) {
        return accountInformation.accountCreator;
      },
      address({ accountInformation }) {
        return accountInformation.address;
      },
      tenancyStartDate({ accountInformation }) {
        return accountInformation.tenancyStartDate;
      },
      tenancyEndDate({ accountInformation }) {
        return accountInformation.tenancyEndDate;
      },
      broadbandInstallationDate({ accountInformation }) {
        return accountInformation.broadbandDate;
      },
      discountDescription({ accountInformation }) {
        return accountInformation.usedDiscount
          ? accountInformation.discountDescription
          : null;
      },
      isUsedDiscount({ accountInformation }) {
        return accountInformation.usedDiscount;
      },
      discount: (state) => state.discount,
    }),
    ...mapGetters({
      pricePerPerson: "pricePerPerson",
      pricePerPersonMonth: "pricePerPersonPerMonth",
      nonDiscountedPricePerPerson: "nonDiscountedPricePerPerson",
      nonDiscountedPricePerPersonMonth: "nonDiscountedPricePerPersonPerMonth",
    }),
    ...mapGetters(["pricePerPerson", "nonDiscountedPricePerPerson"]),
    pricePerPersonPerWeek() {
      return (this.pricePerPerson / 100).toFixed(2);
    },
    pricePerPersonMonthly() {
      let amountInPounds = this.pricePerPersonMonth / 100;
      return (Math.ceil(amountInPounds * 100) / 100).toFixed(2);
    },
    nonDiscountedPricePerPersonPerWeek() {
      return this.isUsedDiscount
        ? (this.nonDiscountedPricePerPerson / 100).toFixed(2)
        : null;
    },
    nonDiscountedPricePerPersonPerMonthly() {
      let amountInPounds = this.nonDiscountedPricePerPersonMonth / 100;
      let amount = Math.ceil(amountInPounds * 100) / 100;
      return this.isUsedDiscount ? amount : null;
    },
  },
  methods: {
    ...mapActions(["getAccountInformation"]),
    ...mapMutations({
      hideBackButton: "HIDE_BACK_BTN",
      showLoadingPanel: "SHOW_LOADING_PANEL",
      hideLoadingPanel: "HIDE_LOADING_PANEL",
      setAccountInformation: "SET_ACCOUNT_INFORMATION",
    }),
    formSubmit() {
      this.trackGoogleEvent(
        "Click Let's Go",
        "Joining Housemate",
        "Join account",
      );
      this.trackFacebookEvent("Click Let's Go");

      this.$router.push("/joining-tenant/personal-details");
    },
  },
  created() {
    // ensure back btn is hidden
    this.hideBackButton();

    this.trackGoogleEvent(
      "Land on Join landing page",
      "Joining Housemate",
      "Land on join page",
    );
    this.trackFacebookEvent("Land on Join landing page");

    // check for referral code in url and validate against endpoint
    const guid = this.$route.query.guid;

    if (guid) {
      this.showLoadingPanel();

      this.getAccountInformation(guid)
        .then(({ data }) => {
          this.setAccountInformation({
            guid,
            ...data,
          });
          // track successful join code
          this.trackGoogleEvent(
            "Customer clicks through to STB via a valid join link",
            "Campaign & Channel",
            "Valid join link",
          );
          this.trackFacebookEvent(
            "Customer clicks through to STB via a valid join link",
          );
        })
        .catch((error) => {
          if (!error.response) {
            this.errorResponse =
              "Network error accessing the Split The Bills API";
          } else {
            if (error.response.status === 410) {
              this.setAccountInformation({
                guid,
                ...error.response.data,
              });
              // if the account is already 100% complete
              this.$router.push({ name: "house-already-complete" });
            } else {
              this.errorResponse = error.response.data.messages[0];
              // track successful join code
              this.trackGoogleEvent(
                "Customer clicks through to STB via a invalid join link",
                "Campaign & Channel",
                "Invalid join link",
              );
              this.trackFacebookEvent(
                "Customer clicks through to STB via a invalid join link",
              );
            }
          }
        })
        .finally(() => {
          this.hideLoadingPanel();
        });
    } else {
      this.errorResponse = "Cannot detect guid";
    }
  },
  components: {
    ServiceSummary,
    WarningPanel,
  },
};
</script>

<style lang="scss" scoped>
.intro {
  text-align: center;
  line-height: 1.8;
  margin: 0;

  span {
    background-color: $grey-light;
    padding: 0 8px;
  }

  .address {
    display: block;
  }
}

.summary {
  margin: $spacing-medium 0;
}

.join-button {
  margin-bottom: 0;
}
</style>
